import get from 'lodash.get';
import PropTypes from 'prop-types';
import { graphql } from 'react-relay/legacy';
import { defineMessages } from 'dibs-react-intl';
import { handleLocaleUrl } from 'dibs-intl/exports/handleLocaleUrl';
import { AB_TEST_VARIANT_VALUES } from 'dibs-constants/exports/abTestConstants';

export class PdpHead {
    constructor(props) {
        this.props = props;
        this.item = get(this.props, 'viewer.itemRead.0');
        const isPdpUrlTranslationEnabled = this.props.viewer.isPdpUrlTranslationEnabled;
        this.pdpUrl = handleLocaleUrl(
            isPdpUrlTranslationEnabled ? this.item.localizedPdpUrl : this.item.pdpURL,
            this.props.intlVariables.locale
        );
        this.intl = this.props.intl;
        this.baseUrl = this.props.intlVariables.localeDomain;
        this.canonicalUrl = `https://${this.baseUrl}${this.pdpUrl}`;
        this.msgs = defineMessages({
            siteName: {
                id: 'pdpHead.siteName',
                defaultMessage: '1stDibs.com',
            },
        });
        const media = get(this.item, 'photos[0].versions') || [];
        const image = media.find(p => get(p, 'type.name') === 'large') || {};
        this.imageUrl = image.webPath;
    }

    getCondensedPdpData() {
        return {
            title: this.title(),
            meta: this.meta(),
            link: this.link(),
        };
    }

    getPdpData(options = {}) {
        return {
            title: this.title(),
            meta: this.meta(options),
            link: this.link(),
            script: [this.microdata()],
        };
    }

    getCanonicalLink() {
        return { rel: 'canonical', href: this.canonicalUrl };
    }

    metaFacebook() {
        return [
            { name: 'fb:admins', content: this.props.clientConf.facebookAdmin },
            { name: 'fb:app_id', content: this.props.clientConf.facebookAppId },
            { name: 'fb:page_id', content: this.props.clientConf.facebookPageId },
        ];
    }

    metaProductPrice() {
        const { isPriceUponRequest, pdpMeta = {} } = this.item;

        if (!pdpMeta.productPriceAmount || !pdpMeta.productPriceCurrency || isPriceUponRequest) {
            return [];
        }

        return [
            { property: 'product:price:amount', content: pdpMeta.productPriceAmount },
            { property: 'product:price:currency', content: pdpMeta.productPriceCurrency },
        ];
    }

    metaTwitter() {
        const { pdpMeta = {} } = this.item;
        return [
            { name: 'twitter:card', content: 'summary' },
            { name: 'twitter:site', content: '@1stdibs' },
            { name: 'twitter:url', content: this.canonicalUrl },
            { name: 'twitter:title', content: pdpMeta.ogTitle },
            { name: 'twitter:description', content: pdpMeta.description },
            { name: 'twitter:image', content: this.imageUrl },
        ];
    }

    title() {
        const { pdpMeta = {} } = this.item;
        return pdpMeta.title;
    }

    metaRobots() {
        const { pdpMeta = {} } = this.item;

        // Add noindex if sitepage service returns false for index.
        if (!pdpMeta?.index) {
            return { name: 'robots', content: 'noindex' };
        }

        return {};
    }

    meta({ hideDescription, metaMore = [] } = {}) {
        return [
            ...(hideDescription ? [] : [this.metaDescription()]),
            ...this.metaFacebook(),
            ...this.metaOg(),
            ...this.metaTwitter(),
            ...this.metaProductPrice(),
            this.metaRobots(),
            ...metaMore,
        ];
    }

    metaDescription() {
        const { pdpMeta = {} } = this.item;
        return { name: 'description', content: pdpMeta.description };
    }

    metaOg() {
        return [
            { name: 'og:site_name', content: this.intl.formatMessage(this.msgs.siteName) },
            { name: 'og:title', content: get(this.item, 'pdpMeta.ogTitle') },
            { name: 'og:url', content: this.canonicalUrl },
            { name: 'og:type', content: 'website' },
            { name: 'og:image', content: this.imageUrl },
        ];
    }

    link() {
        return [this.getCanonicalLink()];
    }

    microdata() {
        const { microdata, microdataWithImageObj } = this.item;
        const { pdpImageSeoTestGroup } = this.props;

        if (pdpImageSeoTestGroup === AB_TEST_VARIANT_VALUES.VARIANT2 && microdataWithImageObj) {
            return {
                type: 'application/ld+json',
                innerHTML: microdataWithImageObj,
            };
        } else if (microdata) {
            return {
                type: 'application/ld+json',
                innerHTML: microdata,
            };
        }

        return {};
    }
}

PdpHead.propTypes = {
    clientConf: PropTypes.object.isRequired,
    intlVariables: PropTypes.shape({
        locale: PropTypes.string,
        localeDomain: PropTypes.string,
    }).isRequired,
};

// eslint-disable-next-line no-unused-vars
const viewerFragment = graphql`
    fragment PdpHead_viewer on Viewer {
        ... @include(if: $fetchPdp) {
            isPdpUrlTranslationEnabled: isEnabled(key: "translatedPdpUrls")
        }
    }
`;

// eslint-disable-next-line no-unused-vars
const itemFragment = graphql`
    fragment PdpHead_item on Item {
        microdata(currency: $forcedCurrency)
        microdataWithImageObj: microdata(currency: $forcedCurrency, addImageObj: true)
        pdpURL
        localizedPdpUrl
        pdpMeta {
            index
            title
            description
            ogTitle
            productPriceAmount
            productPriceCurrency
        }
        photos {
            versions {
                webPath
                type {
                    name
                }
            }
        }
        isPriceUponRequest
    }
`;
